export interface EmailSettingValue {
  sender?: "mailbox" | "system";
  on_token_expired?: "system" | "wait" | "stop";
}

export interface ResolvedEmailSetting extends EmailSettingValue {
  editable: boolean;
}

export interface EmailSettingsResponse {
  email_settings: Record<string, EmailSettingValue>;
  resolved: {
    send_gift: ResolvedEmailSetting;
    referral: ResolvedEmailSetting;
    reminder: ResolvedEmailSetting;
    occasional: ResolvedEmailSetting;
    transactional: ResolvedEmailSetting;
  };
  mailbox_connected: boolean;
  mailbox_type?: string;
  system_from: string;
}

export interface DefaultSettings {
  occasion_id: string | null;
  email_template_id: string | null;
  gift_type: string | null;
  price_id: string | null;
  handwritten_note: string | null;
}

export interface ReviewLink {
  review_link_id: string;
  name?: string;
  display_name: string;
  displayName?: string;
  link: string;
  type: string;
  user_id: string;
  is_delete: boolean;
  created_at: string;
  updated_at: string;
}

export interface User {
  user_id: string;
  email: string;
  role: string;
  first_name: string;
  last_name: string;
  full_name: string;
  invite_code: string;
  signature: string;
  organization_id: string;
  organization: Organization;
  mobile_number: string;
  country_code?: string;
  date_of_birth: string;
  service_description: string;
  default_price_id: string;
  is_international_shipping: boolean;
  is_fallback_gift: boolean;
  is_onboarded: boolean;
  picture?: string;
  is_sender_list?: boolean;
  is_sender_card_option?: boolean;
  is_referral?: boolean;
  user_hash?: string | null;
  referral_hash?: string | null;
  referral_message?: string | null;
  mailbox_type: MailboxType;
  scope?: string;
  is_mailbox_token_expired?: boolean;
}

export interface Organization {
  organization_id: string;
  name: string;
  contactEmail: string;
  contactPhone: string;
  website: string;
  facebook_url: string;
  instagram_url: string;
  twitter_url: string;
  linkedin_url: string;
  currency: string;
  payment_setting: string;
  createdAt: string;
  updatedAt: string;
  subscriptions: Subscription[];
}

export enum SubscriptionType {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  QUARTERLY = 'quarterly',
  HALF_YEARLY = 'half_yearly',
}

export interface Subscription {
  subscription_id: string;
  plan_name: string;
  stripe_subscription_id?: string;
  plan_amount: string;
  status: string;
  started_on?: string | null;
  ended_on?: string | null;
  status_updated_on?: string | null;
  last_charged_on?: string | null;
  subscription_type?: SubscriptionType | null;
  trial_days: number;
  organization_id: string;
  created_at: string;
  updated_at: string;
}

export interface Referral {
  referral_id: string;
  first_emailer_days: number;
  first_emailer_email_template_body: string;
  second_emailer_days: number;
  second_emailer_email_template_body: string;
  enabled: boolean;
  user_id: string;
  is_delete: boolean;
  added_on: string;
  updated_at: string;
  first_emailer_subject: string;
  second_emailer_subject: string;
  referral_sender?: ReferralSender;
}

export interface ReferralSender {
  sender?: "mailbox" | "system";
  on_token_expired?: "system" | "wait";
}

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
}

export enum MailboxType {
  GOOGLE = 'google',
  MICROSOFT = 'microsoft',
  NOT_CONNECTED = 'not_connected',
}

export enum PaymentSetting {
  SAME_PAYMENT_METHOD = 'same_payment_method',
  SEPARATE_PAYMENT_METHOD = 'separate_payment_method',
  CUSTOM_PAYMENT_METHOD = 'custom_payment_method',
}

export interface TeamUser {
  user_id?: string;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name: string;
  role: string;
  created_at: string;
  is_active: boolean;
  /** True while invite email is sent but user has not completed signup (stub row). */
  invitation_pending?: boolean;
}